import * as React from 'react';
import { cn } from '@/lib/utils';

export type { GlassSelectOption, GlassSelectProps } from '@/components/ui/glass-select';
export { GlassSelect } from '@/components/ui/glass-select';

/**
 * Conjunto enxuto de primitives de formulario usadas pelas secoes do
 * cadastro de cliente. Substituem o overhead de pegar shadcn completo
 * so para essa tela. Visual alinhado ao restante do app (tokens CSS).
 */

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
  htmlFor?: string;
}

export function Field({
  label,
  required,
  error,
  hint,
  className,
  children,
  htmlFor,
}: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={htmlFor}
        className="text-xs font-medium text-[var(--fg-muted)]"
      >
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-[11px] text-red-500">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-[var(--fg-muted)] opacity-70">{hint}</p>
      ) : null}
    </div>
  );
}

export const TextInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => (
  <input
    ref={ref}
    {...props}
    className={cn(
      'flex h-9 w-full rounded-md border bg-transparent px-2.5 text-sm',
      'border-[var(--border)] text-[var(--fg)]',
      'placeholder:text-[var(--fg-muted)]',
      'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
  />
));
TextInput.displayName = 'TextInput';

export const TextArea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    {...props}
    className={cn(
      'flex w-full rounded-md border bg-transparent px-2.5 py-2 text-sm',
      'border-[var(--border)] text-[var(--fg)]',
      'placeholder:text-[var(--fg-muted)]',
      'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
  />
));
TextArea.displayName = 'TextArea';


/** Grid 2 colunas no md, 1 coluna no mobile. */
export function FormGrid({
  children,
  cols = 2,
}: {
  children: React.ReactNode;
  cols?: 1 | 2 | 3;
}) {
  const colClass =
    cols === 3
      ? 'md:grid-cols-3'
      : cols === 2
        ? 'md:grid-cols-2'
        : 'md:grid-cols-1';
  return (
    <div className={cn('grid grid-cols-1 gap-4', colClass)}>{children}</div>
  );
}

export function SectionTitle({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="mb-1 flex flex-col gap-0.5">
      <h3 className="text-base font-semibold text-[var(--fg)]">{title}</h3>
      {description && (
        <p className="text-xs text-[var(--fg-muted)]">{description}</p>
      )}
    </div>
  );
}
